import { useState, useRef, useEffect } from "react";
import { ChatCircleDots, PaperPlaneRight, X, SpinnerGap, Lightning, CalendarBlank, MagnifyingGlass, CurrencyEur, Phone, WhatsappLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";

const STARTERS = [
  { icon: "🪨", text: "Was kostet eine Steinschlagreparatur?" },
  { icon: "🚗", text: "Wie lange dauert ein Scheibentausch?" },
  { icon: "🛡️", text: "Übernimmt die Versicherung die Kosten?" },
  { icon: "📋", text: "Welche Unterlagen brauche ich?" },
  { icon: "⏰", text: "Bekomme ich heute noch einen Termin?" },
  { icon: "💳", text: "Ich habe keine Versicherung — was kostet das?" },
];

const QUICK_ACTIONS = [
  { label: "Schaden melden", icon: Lightning, route: "/schaden-melden", color: "bg-blue-600 hover:bg-blue-700" },
  { label: "Termin buchen", icon: CalendarBlank, route: "/schaden-melden", color: "bg-slate-900 hover:bg-slate-800" },
  { label: "Selbstzahler-Preis", icon: CurrencyEur, route: "/selbstzahler", color: "bg-emerald-600 hover:bg-emerald-700" },
  { label: "Status verfolgen", icon: MagnifyingGlass, route: "/status", color: "bg-amber-500 hover:bg-amber-600" },
];

const getSessionId = () => {
  let id = localStorage.getItem("chat_session_id");
  if (!id) {
    id = "chat-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("chat_session_id", id);
  }
  return id;
};

export const Chatbot = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hallo! Ich bin Ihr KI-Berater von AutoGlas Erol. Wie kann ich Ihnen helfen? Sie können mir Ihre Frage stellen oder direkt eine Aktion wählen.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  if (location.pathname.startsWith("/admin")) return null;

  const send = async (text) => {
    if (!text.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", {
        session_id: sessionId.current,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Entschuldigung, gerade nicht erreichbar. Bitte rufen Sie 0621 527429 an oder schreiben Sie per WhatsApp." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const goTo = (route) => {
    setOpen(false);
    navigate(route);
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-24 right-5 z-50 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30 flex items-center justify-center transition-all hover:scale-105"
        data-testid="chatbot-toggle"
        aria-label="Chat öffnen"
      >
        {open ? <X size={26} weight="bold" /> : <ChatCircleDots size={26} weight="fill" />}
      </button>

      {open && (
        <div
          className="fixed bottom-44 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[400px] h-[600px] max-h-[calc(100dvh-13rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-fade-up"
          data-testid="chatbot-panel"
        >
          <div className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center">
                  <ChatCircleDots size={18} weight="fill" />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-slate-900 pulse-dot" />
              </div>
              <div>
                <div className="font-semibold text-sm">AutoGlas KI-Berater</div>
                <div className="text-[10px] text-slate-300">KI-Assistent · automatisierte Antworten</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-800 rounded">
              <X size={18} />
            </button>
          </div>

          {/* Quick Actions */}
          <div className="px-3 pt-3 pb-2 bg-slate-50 border-b border-slate-200">
            <div className="grid grid-cols-2 gap-2">
              {QUICK_ACTIONS.map((a) => (
                <button
                  key={a.label}
                  onClick={() => goTo(a.route)}
                  className={`${a.color} text-white text-xs font-semibold rounded-lg px-3 py-2 flex items-center gap-1.5 justify-center transition-colors`}
                  data-testid={`chat-action-${a.label}`}
                >
                  <a.icon size={14} weight="fill" />
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-blue-600 text-white rounded-br-md"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm"
                  }`}
                  data-testid={`chat-msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 shadow-sm">
                  <SpinnerGap size={18} className="text-slate-400 animate-spin" />
                </div>
              </div>
            )}
            {messages.length <= 1 && (
              <div className="pt-2">
                <p className="text-xs text-slate-500 mb-2 font-medium">💬 Häufige Fragen:</p>
                <div className="flex flex-col gap-2">
                  {STARTERS.map((s) => (
                    <button
                      key={s.text}
                      onClick={() => send(s.text)}
                      className="text-left text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50/30 transition-colors"
                      data-testid={`chat-starter-${s.text}`}
                    >
                      <span className="mr-1.5">{s.icon}</span>{s.text}
                    </button>
                  ))}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-200 grid grid-cols-2 gap-2">
                  <a href="tel:+49621527429" className="flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg py-2 hover:border-slate-300" data-testid="chat-cta-call">
                    <Phone size={14} weight="fill" /> +49 621 527429
                  </a>
                  <a href="https://wa.me/4915222717582" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg py-2" data-testid="chat-cta-whatsapp">
                    <WhatsappLogo size={14} weight="fill" /> WhatsApp
                  </a>
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="border-t border-slate-200 p-2 flex items-center gap-2 bg-white"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Frage eingeben…"
              className="flex-1 h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm"
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              className="h-10 w-10 p-0 bg-blue-600 hover:bg-blue-700"
              data-testid="chat-send"
            >
              <PaperPlaneRight size={18} weight="fill" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};

export default Chatbot;
